import React, { useState, useEffect } from 'react';
import { Calendar, Upload, UserPlus, Plus, Users, Clock, ChevronRight, Home, Scan, Import, CircleCheckBig, MonitorCheck } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import ClockComponent from '@/components/custom/utilities/clock';
// Mock BackendTemplateConfig
const BackendTemplateConfig = {
  container: {
    minHeight: 'min-h-screen',
    backgroundGradient: 'bg-gradient-to-br from-gray-50 to-gray-100',
    paddingLarge: 'p-8',
    maxWidth: 'max-w-7xl',
    marginX: 'mx-auto'
  },
  typography: {
    pageTitle: 'text-4xl font-bold text-gray-800',
    sectionTitle: 'text-2xl font-semibold text-gray-800',
    heading: 'text-lg font-semibold text-gray-800',
    body: 'text-gray-600',
    small: 'text-sm text-gray-500'
  },
  card: {
    base: 'bg-white rounded-xl shadow-lg',
    padding: 'p-4',
    paddingLarge: 'p-6',
    spacingLarge: 'space-y-4'
  },
  layout: {
    flex: 'flex',
    flexStart: 'flex items-start',
    flexBetween: 'flex items-center justify-between',
    flexCenter: 'flex items-center justify-center'
  },
  spacing: {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6'
  },
  icons: {
    medium: 'w-5 h-5',
    normal: 'w-6 h-6',
    large: 'w-8 h-8'
  },
  colors: {
    primary: {
      text: 'text-blue-600',
      100: 'bg-blue-100',
      500: 'bg-blue-500',
      600: 'to-blue-600'
    },
    success: {
      500: 'bg-green-500',
      600: 'to-green-600'
    },
    warning: {
      500: 'bg-amber-500'
    },
    danger: {
      500: 'bg-red-500'
    },
    neutral: {
      50: 'bg-gray-50',
      200: 'bg-gray-200',
      text: {
        light: 'text-gray-400'
      },
      border: {
        light: 'border-gray-200'
      }
    },
    white: 'border-white'
  },
  borderRadius: {
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full'
  },
  borders: {
    default: 'border'
  },
  animations: {
    hover: {
      shadow: 'hover:shadow-xl',
      scale: 'hover:scale-105'
    },
    transition: {
      all: 'transition-all',
      transform: 'transition-transform',
      opacity: 'transition-opacity'
    },
    duration: {
      normal: 'duration-300'
    }
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  const [greeting, setGreeting] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const config = BackendTemplateConfig;

  const getDisplayName = () => {
    // Debug: log user object to check available fields
    console.log('Dashboard - User object:', user);
    
    if (user?.first_name) {
      return user.first_name;
    }
    return 'User';
  };

  const userName = getDisplayName();
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 17) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  // Upcoming activities data
  const upcomingActivities = [
    { 
      id: 1, 
      title: 'Implement import workflow', 
      priority: 'high',
      status: 'in-progress',
      dueDate: 'Today'
    },
    { 
      id: 2, 
      title: 'Implement logo insertion workflow', 
      priority: 'medium',
      status: 'pending',
      dueDate: 'Tomorrow'
    },
    { 
      id: 3, 
      title: 'Implement main workflow logic', 
      priority: 'high',
      status: 'pending',
      dueDate: 'This Week'
    },
  ];

  // Menu items from sidebar as quick actions
  const actionCards = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: Home,
      color: config.colors.primary,
      description: 'View your main dashboard',
      path: '/dashboard'
    },
    {
      id: 'system-status',
      title: 'System Status',
      icon: MonitorCheck,
      color: config.colors.success,
      description: 'Check system health and status',
      path: '/system-status'
    },
    {
      id: 'data-ingest',
      title: 'Data Ingest',
      icon: Import,
      color: config.colors.warning,
      description: 'Import and manage data',
      path: '/data/ingest'
    },
    {
      id: 'images-original',
      title: 'Original Images',
      icon: Scan,
      color: config.colors.danger,
      description: 'View original images',
      path: '/images/original'
    },
    {
      id: 'final-review',
      title: 'Final Review',
      icon: CircleCheckBig,
      color: { 500: 'bg-purple-500' },
      description: 'Review and approve items',
      path: '/review/final'
    }
  ];

  const handleAction = (actionId, path) => {
    console.log(`Action triggered: ${actionId}, path: ${path}`);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-amber-100 text-amber-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`${config.container.minHeight} ${config.container.backgroundGradient} ${config.container.paddingLarge}`}>
      <div className={`${config.container.maxWidth} ${config.container.marginX}`}>
        
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className={`${config.typography.pageTitle} mb-2`}>
            {greeting}! 👋
          </h1>
          <p className={config.typography.body}>Welcome back to your dashboard</p>
        </div>

        {/* Clock and Activities Section */}
        <div className={`mb-8 ${config.layout.flex} ${config.spacing.lg}`}>
          
          {/* Clock Section - 1/4 width */}
          <div className="w-1/4">
            <ClockComponent />
          </div>

          {/* Upcoming Activities Section - 3/4 width */}
          <div className={`w-3/4 ${config.card.base} ${config.card.paddingLarge}`}>
            <div className={`${config.layout.flexStart} mb-4`}>
              <Clock className={`${config.icons.large} ${config.colors.primary.text} mr-2`} />
              <h2 className={config.typography.sectionTitle}>Upcoming Activities</h2>
            </div>
            
            <div className={config.card.spacingLarge}>
              {upcomingActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className={`${config.layout.flexBetween} ${config.card.padding} ${config.colors.neutral[50]} ${config.borderRadius.lg} ${config.borders.default} ${config.colors.neutral.border.light} ${config.animations.hover.shadow} ${config.animations.transition.all} ${config.animations.duration.normal} cursor-pointer hover:scale-[1.02]`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`${config.layout.flex} items-center ${config.spacing.md}`}>
                    <div className={`w-12 h-12 ${config.colors.primary[100]} ${config.borderRadius.full} ${config.layout.flexCenter}`}>
                      <CircleCheckBig className={`${config.icons.normal} ${config.colors.primary.text}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={config.typography.heading}>{activity.title}</h3>
                      <p className={config.typography.small}>Due: {activity.dueDate}</p>
                    </div>
                  </div>
                  
                  <div className={`${config.layout.flex} items-center ${config.spacing.sm}`}>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(activity.priority)} mr-2`}>
                      {activity.priority}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(activity.status)}`}>
                      {activity.status}
                    </span>
                    <ChevronRight className={`${config.icons.normal} ${config.colors.neutral.text.light} ml-2`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Cards Section */}
        <div className="mb-8">
          <h2 className={`${config.typography.sectionTitle} mb-4`}>Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {actionCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  className={`relative group cursor-pointer transform ${config.animations.transition.all} ${config.animations.duration.normal} ${config.animations.hover.scale}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onMouseEnter={() => setHoveredCard(card.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => handleAction(card.id, card.path)}
                >
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 ${config.animations.transition.opacity} ${config.animations.duration.normal} ${config.borderRadius.xl} blur-xl ${card.color[500]}`} />
                  
                  <div className={`relative ${config.card.base} ${config.card.paddingLarge} ${config.animations.hover.shadow} ${config.animations.transition.all} ${config.animations.duration.normal}`}>
                    <div className={`w-14 h-14 ${card.color[500]} ${config.borderRadius.lg} ${config.layout.flexCenter} mb-4 group-hover:scale-110 ${config.animations.transition.transform} ${config.animations.duration.normal}`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className={`${config.typography.heading} mb-2`}>{card.title}</h3>
                    <p className={`${config.typography.body} mb-4`}>{card.description}</p>
                    <div className={`${config.layout.flex} items-center ${config.colors.primary.text} font-semibold group-hover:translate-x-2 ${config.animations.transition.transform} ${config.animations.duration.normal}`}>
                      <span className="text-sm">Get Started</span>
                      <ChevronRight className={`${config.icons.medium} ml-1`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;